import { ChoiceBoard } from '@shared/schema';

// Store last notification times for each board to implement cooldown periods
interface NotificationState {
  boardId: number;
  lastNotificationTime: number; // timestamp in ms
  isInDwellPeriod: boolean;
  dwellStartTime: number; // timestamp in ms
  userLocation: {
    lat: number;
    lng: number;
    speed: number | null;
  } | null;
}

class ChoiceBoardNotificationManager {
  private static instance: ChoiceBoardNotificationManager;
  private notificationStates: Map<number, NotificationState>;
  private isTrackingLocation: boolean;
  private watchId: number | null;
  
  private constructor() {
    this.notificationStates = new Map();
    this.isTrackingLocation = false;
    this.watchId = null;
    
    // Try to restore notification states from localStorage to persist across page refreshes
    try {
      const savedStates = localStorage.getItem('choiceBoardNotificationStates');
      if (savedStates) {
        const parsedStates: [number, NotificationState][] = JSON.parse(savedStates);
        this.notificationStates = new Map(parsedStates);
      }
    } catch (error) {
      console.error('Error restoring notification states from localStorage:', error);
    }
  }
  
  public static getInstance(): ChoiceBoardNotificationManager {
    if (!ChoiceBoardNotificationManager.instance) {
      ChoiceBoardNotificationManager.instance = new ChoiceBoardNotificationManager();
    }
    return ChoiceBoardNotificationManager.instance;
  }
  
  // Save notification states to localStorage
  private saveState(): void {
    try {
      localStorage.setItem(
        'choiceBoardNotificationStates', 
        JSON.stringify(Array.from(this.notificationStates.entries()))
      );
    } catch (error) {
      console.error('Error saving notification states to localStorage:', error);
    }
  }
  
  // Start location tracking for notifications
  public startLocationTracking(): void {
    if (this.isTrackingLocation || !navigator.geolocation) return;
    
    console.log('Starting location tracking for choice board notifications');
    
    this.isTrackingLocation = true;
    
    // Start watching position with high accuracy for better location-based notifications
    this.watchId = navigator.geolocation.watchPosition(
      (position) => this.handlePositionUpdate(position),
      (error) => {
        console.error('Error getting location for choice board notifications:', error);
        this.isTrackingLocation = false;
        this.watchId = null;
      },
      { 
        enableHighAccuracy: true, 
        maximumAge: 30000, // 30 seconds
        timeout: 27000 // 27 seconds
      }
    );
  }
  
  // Stop location tracking
  public stopLocationTracking(): void {
    if (!this.isTrackingLocation || this.watchId === null) return;
    
    console.log('Stopping location tracking for choice board notifications');
    
    navigator.geolocation.clearWatch(this.watchId);
    this.isTrackingLocation = false;
    this.watchId = null;
  }
  
  // Handle position updates from the geolocation watcher
  private handlePositionUpdate(position: GeolocationPosition): void {
    const { latitude, longitude, speed } = position.coords;
    
    // Fetch all available choice boards that have location settings enabled
    fetch(`/api/choice-boards/by-location?lat=${latitude}&lng=${longitude}`)
      .then(response => response.json())
      .then((boards: ChoiceBoard[]) => {
        // Process each board for notifications
        boards.forEach(board => this.processBoardNotification(board, latitude, longitude, speed || 0));
      })
      .catch(error => {
        console.error('Error fetching location-based choice boards:', error);
      });
  }
  
  // Process a board to determine if a notification should be sent
  private processBoardNotification(
    board: ChoiceBoard, 
    lat: number, 
    lng: number, 
    speed: number
  ): void {
    // Skip if notifications are disabled for this board
    if (!board.enableNotifications) return;
    
    const now = Date.now();
    const boardId = board.id;
    const notificationState = this.notificationStates.get(boardId);
    
    // Initialize state if not exists
    if (!notificationState) {
      this.notificationStates.set(boardId, {
        boardId,
        lastNotificationTime: 0,
        isInDwellPeriod: false,
        dwellStartTime: now,
        userLocation: { lat, lng, speed: speed || null }
      });
      this.saveState();
      
      // Log this location visit for frequency learning
      this.recordLocationVisit(board, lat, lng);
      return;
    }
    
    // Update user location in state
    notificationState.userLocation = { lat, lng, speed: speed || null };
    
    // Calculate cooldown - don't notify if we're in the cooldown period
    const cooldownPeriod = (board.notificationCooldown || 86400) * 1000; // convert seconds to ms
    const dwellTime = (board.notificationDwellTime || 120) * 1000; // convert seconds to ms
    
    // If currently in cooldown period, don't proceed
    if (now - notificationState.lastNotificationTime < cooldownPeriod) {
      return;
    }
    
    // Enhanced movement detection with smart filtering
    // If speed is above walking pace (> 2 m/s or ~4.5 mph), it's likely the user is in transit
    // Reset dwell timer in this case so we only notify when they've stopped
    if (this.isUserInTransit(speed, notificationState)) {
      notificationState.isInDwellPeriod = false;
      notificationState.dwellStartTime = now;
      this.saveState();
      return;
    }
    
    // Record this location visit for frequency learning
    this.recordLocationVisit(board, lat, lng);
    
    // Start dwell period if not already started
    if (!notificationState.isInDwellPeriod) {
      notificationState.isInDwellPeriod = true;
      notificationState.dwellStartTime = now;
      this.saveState();
      return;
    }
    
    // If in dwell period but not enough time has passed, wait
    if (now - notificationState.dwellStartTime < dwellTime) {
      return;
    }
    
    // All conditions met, send notification!
    this.sendNotification(board);
    
    // Update notification state
    notificationState.lastNotificationTime = now;
    notificationState.isInDwellPeriod = false;
    this.saveState();
  }
  
  // Smart movement detection based on speed and patterns
  private isUserInTransit(speed: number, state: NotificationState): boolean {
    // Basic transit detection - if speed is above walking pace
    if (speed > 2) {
      return true;
    }
    
    // If we have previous location data, we can do more advanced detection
    if (state.userLocation) {
      // If speed is suddenly much lower but still moving, they might be slowing down temporarily
      // (e.g., at a traffic light but still in transit)
      const prevSpeed = state.userLocation.speed || 0;
      if (prevSpeed > 5 && speed < 2 && speed > 0.5) {
        // They were moving fast and now slowed down but still moving
        // Let's wait a bit longer before considering them stopped
        return true;
      }
    }
    
    return false;
  }
  
  // Record location visits to learn patterns
  private recordLocationVisit(board: ChoiceBoard, lat: number, lng: number): void {
    try {
      // Get or initialize frequency data
      const frequentLocationsKey = 'choiceBoardFrequentLocations';
      let frequentLocations = {};
      
      const storedData = localStorage.getItem(frequentLocationsKey);
      if (storedData) {
        frequentLocations = JSON.parse(storedData);
      }
      
      // Create a location ID based on rounded coordinates (to group nearby locations)
      // Round to ~100m precision
      const roundFactor = 1000; // ~100 meter precision
      const locationId = `${Math.round(lat * roundFactor) / roundFactor},${Math.round(lng * roundFactor) / roundFactor}`;
      
      // Initialize or update the visit count for this location
      if (!frequentLocations[locationId]) {
        frequentLocations[locationId] = {
          count: 1,
          lastVisit: Date.now(),
          coordinates: { lat, lng },
          boards: [board.id]
        };
      } else {
        frequentLocations[locationId].count += 1;
        frequentLocations[locationId].lastVisit = Date.now();
        
        // Add this board to the location's boards if not already there
        if (!frequentLocations[locationId].boards.includes(board.id)) {
          frequentLocations[locationId].boards.push(board.id);
        }
      }
      
      // Save back to localStorage
      localStorage.setItem(frequentLocationsKey, JSON.stringify(frequentLocations));
      
      // If this location has been visited frequently, suggest creating a board
      // Only do this after multiple visits and if there are few boards associated
      if (frequentLocations[locationId].count >= 3 && 
          frequentLocations[locationId].boards.length <= 2) {
        this.suggestChoiceBoardForLocation(frequentLocations[locationId]);
      }
    } catch (error) {
      console.error('Error recording location visit:', error);
    }
  }
  
  // Suggest creating a choice board for a frequently visited location
  private suggestChoiceBoardForLocation(locationData: any): void {
    // Only suggest once per day per location
    const now = Date.now();
    const oneDayMs = 24 * 60 * 60 * 1000;
    
    // Get or initialize suggestion timestamps
    const suggestionsKey = 'choiceBoardLocationSuggestions';
    let suggestions = {};
    
    const storedData = localStorage.getItem(suggestionsKey);
    if (storedData) {
      suggestions = JSON.parse(storedData);
    }
    
    const locationId = `${locationData.coordinates.lat},${locationData.coordinates.lng}`;
    
    // Check if we've suggested recently
    if (suggestions[locationId] && now - suggestions[locationId] < oneDayMs) {
      return;
    }
    
    // Store the suggestion time
    suggestions[locationId] = now;
    localStorage.setItem(suggestionsKey, JSON.stringify(suggestions));
    
    // Create a suggestion notification if supported
    if ("Notification" in window && Notification.permission === "granted") {
      const notification = new Notification('Frequent Location Detected', {
        body: `You visit this location often. Would you like to create a Choice Board for it?`,
        icon: '/logo.png',
        data: {
          type: 'location_suggestion',
          coordinates: locationData.coordinates,
          timestamp: now
        }
      });
      
      notification.onclick = () => {
        window.focus();
        // Store the coordinates to pre-fill in the new choice board form
        localStorage.setItem('suggestedLocation', JSON.stringify(locationData.coordinates));
        window.location.href = '/choice-boards?action=create';
        notification.close();
      };
    }
  }
  
  // Send the actual notification to the user
  private sendNotification(board: ChoiceBoard): void {
    const title = `${board.locationName || 'Choice Board'} Available`;
    const body = board.description || 'A choice board is available for this location';
    this.dispatchNotification(title, body, board.id);
  }

  /**
   * Public entry point for nearby-board alerts.
   * Uses the same permission-check and creation path as all other choice board notifications.
   */
  public sendNearbyAlert(boardId: string | number, boardTitle: string): void {
    this.dispatchNotification("You're nearby", boardTitle, boardId);
  }

  // Central permission-check + notification dispatch — used by all notification paths
  private dispatchNotification(title: string, body: string, boardId: string | number): void {
    if (!("Notification" in window)) {
      console.warn('This browser does not support desktop notifications');
      return;
    }

    const create = () => this.createNotification(title, body, boardId);

    if (Notification.permission === "granted") {
      create();
    } else if (Notification.permission !== "denied") {
      Notification.requestPermission().then(permission => {
        if (permission === "granted") create();
      });
    }
  }

  // Create the browser notification
  private createNotification(title: string, body: string, boardId: string | number): void {
    try {
      const options = {
        body,
        icon: '/logo.png',
        vibrate: [100, 50, 100],
        data: {
          boardId,
          timestamp: Date.now()
        }
      };
      
      const notification = new Notification(title, options);
      
      notification.onclick = () => {
        window.focus();
        window.location.href = `/choice-boards`;
        notification.close();
      };
      
      console.log(`Sent notification for board ${boardId}: ${title}`);
    } catch (error) {
      console.error('Error creating notification:', error);
    }
  }
  
  // Reset a specific board's notification state
  public resetBoardNotificationState(boardId: number): void {
    this.notificationStates.delete(boardId);
    this.saveState();
  }
  
  // Reset all notification states
  public resetAllNotificationStates(): void {
    this.notificationStates.clear();
    this.saveState();
  }
  
  // Get the current notification state for a board
  public getBoardNotificationState(boardId: number): NotificationState | undefined {
    return this.notificationStates.get(boardId);
  }
}

// Export a singleton instance
export const choiceBoardNotifications = ChoiceBoardNotificationManager.getInstance();

// Helper function to calculate distance between coordinates (haversine formula)
export function calculateDistance(
  lat1: number, 
  lon1: number, 
  lat2: number, 
  lon2: number
): number {
  const R = 6371e3; // Earth's radius in meters
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;

  const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
            Math.cos(φ1) * Math.cos(φ2) *
            Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  
  return R * c; // Distance in meters
}